import { Link } from 'react-router-dom'
import { ExtLink } from '../components/ExtLink'

/**
 * Standalone technical-details page. Loosely coupled to the rest of the app:
 * only depends on ExtLink and react-router Link. To remove this page, delete
 * this file plus the route + nav entry; nothing else references it.
 */

export function Tech() {
  return (
    <div className="max-w-3xl mx-auto py-6">
      <div className="border border-border rounded p-3 bg-surface text-xs text-text-dim mb-6 leading-relaxed">
        Bu sayfa, projenin teknik detaylarına ilgi duyan okuyucular için yazılmıştır.
        Yoğun terminoloji ve sayı içerir. Sıradan bir okuyucunun bu sayfayı okumasına
        gerek yoktur. Daha sade bir özet için{' '}
        <Link to="/hakkinda" className="text-accent">Hakkında</Link> sayfasına,
        kullanılan kavramların tanımları için{' '}
        <Link to="/hakkinda#sozluk" className="text-accent">Sözlük</Link> bölümüne
        bakabilirsiniz.
      </div>

      <h1 className="text-2xl sm:text-3xl font-semibold text-text mb-2">Teknik detaylar</h1>
      <p className="text-text-dim mb-8 leading-relaxed">
        Bu sayfada Sezi’nin iş akışı baştan sona, ön işleme, mimari, eğitim ayarları,
        çapraz doğrulama yöntemi, denenip reddedilen yaklaşımlar, kohortlar arası
        davranış, kalibrasyon sorunu ve gelecek geliştirme planı dahil olmak üzere bütün
        teknik detaylarıyla anlatılmaktadır.
      </p>

      <Sec id="ozet" title="Tek bakışta">
        <p>
          Sezi, kontrastlı meme MR görüntülerinden lezyon segmentasyonu, 3 boyutlu yüzey
          oluşturma ve iyi/kötü huylu sınıflandırması yapan üç aşamalı bir iş akışı
          sunar.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Segmentasyon: <Code>nnU-Net v2</Code>, PlainConvUNet 6 aşamalı 3B mimari.</li>
          <li>3B yüzey oluşturma: yürüyen küpler + Taubin yumuşatma + glTF dışa aktarımı.</li>
          <li>
            Sınıflandırıcı: aynı segmentasyon kodlayıcısı, üzerine kinetik kanal girdili
            sığ MLP başı.
          </li>
          <li>İki sınıflandırıcı sürümü: yalnız AMBL ve AMBL+BD kombine.</li>
          <li>
            Test zamanı 4 yansıma artırması (TTA) tüm sınıflandırıcı tahminlerinde
            kullanılır.
          </li>
        </ul>
      </Sec>

      <Sec id="on-isleme" title="Görüntü ön işlemesi">
        <p>
          Tüm 3B hacimler aşağıdaki ardışık adımlardan geçer:
        </p>
        <ol className="list-decimal pl-5 space-y-2 text-sm">
          <li>
            <strong className="text-text">Anatomik yeniden yönlendirme (RAS).</strong>{' '}
            <Code>SimpleITK.DICOMOrient(img, "RAS")</Code> ile sol-sağ, ön-arka,
            alt-üst eksenleri tutarlı hale getirilir. Farklı cihaz koordinatlarındaki
            görüntüler aynı eksen düzenine oturur.
          </li>
          <li>
            <strong className="text-text">Yeniden örnekleme.</strong> Hedef voksel
            çözünürlüğü <Code>1.20 × 0.703 × 0.703 mm</Code>. Bu değer, nnU-Net’in eğitim
            kümesini inceleyerek otomatik planlamayla seçtiği ayardır; elle
            değiştirilmemiştir.
            Görüntüler doğrusal interpolasyonla, maskeler en yakın komşuyla
            yeniden örneklenir.
          </li>
          <li>
            <strong className="text-text">Yoğunluk normalizasyonu.</strong> Sınıflandırıcı
            için her kanal kendi içinde <Code>(x − μ) / σ</Code> ile z-skoruna
            dönüştürülür; nnU-Net kendi p1-p99 kırpma + ön plan z-skor normalleştirmesini
            uygular.
          </li>
          <li>
            <strong className="text-text">Yama kırpma.</strong> Sınıflandırıcı için
            lezyon merkezi etrafından <Code>96 × 160 × 160</Code> voksellik bir yama
            alınır. Sınırlardan taşıyorsa <Code>reflect</Code> doldurmasıyla genişletilir.
          </li>
        </ol>
      </Sec>

      <Sec id="segmentasyon" title="Segmentasyon: nnU-Net detayları">
        <p>
          Segmentasyon modeli{' '}
          <ExtLink href="https://github.com/MIC-DKFZ/nnUNet">nnU-Net v2</ExtLink>’nin
          standart 3D-fullres yapılandırmasıyla eğitilmiştir.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Mimari: 6 aşamalı PlainConvUNet, sırasıyla 32, 64, 128, 256, 320, 320 kanal.</li>
          <li>Yama: <Code>96 × 160 × 160</Code>. Parti boyutu 2.</li>
          <li>Kayıp: standart Dice + çapraz entropi (toplam, eşit ağırlıkla).</li>
          <li>Eğitim verisi: yaklaşık 1500 hastalık MAMA-MIA koleksiyonu.</li>
          <li>
            Çapraz doğrulama bölmeleri kohort dışarıda bırakma yaklaşımıyla yapılmıştır:
            kat 0 NACT-dışı, kat 1 DUKE-dışı, kat 2 ISPY1-dışı, kat 3 ISPY2-dışı,
            kat 4 katmanlı rastgele 80/20.
          </li>
          <li>
            <strong className="text-text">Sonuçlar (Dice, ortalama):</strong> NACT-dışı{' '}
            <Code>0.82</Code>, DUKE-dışı <Code>0.57</Code>, katmanlı rastgele{' '}
            <Code>0.78</Code>.
          </li>
          <li>
            DUKE-dışındaki düşük skor kohort kaymasından çok küçük lezyon karışımına
            bağlı; eğitim verisine DUKE eklendiğinde DUKE Dice yalnızca 0.020 yükseliyor.
            Yani sorun kohorta özgü olmaktan çok, lezyon boyutu dağılımıyla ilişkili.
          </li>
          <li>
            Tam kaçırma vakaları: DUKE-dışı kümede 5 vakada Dice <Code>0.0</Code>; ortak
            örüntü merkezden uzak (&gt;79 mm) ve çok odaklı (multifokal) lezyonlar.
          </li>
          <li>
            Sınıflandırma demolarında kat-4 (katmanlı rastgele) modeli kullanılır; bu, en geniş
            eğitim kümesini gören modeldir.
          </li>
        </ul>
      </Sec>

      <Sec id="kinetik" title="Kinetik kanal mühendisliği">
        <p>
          Sınıflandırıcı girdisi 4 kanallıdır. Lezyonun kontrast tutuş davranışı doğrudan
          modele verilir.
        </p>
        <pre className="bg-surface-2 rounded p-3 text-[11px] sm:text-xs overflow-x-auto leading-relaxed whitespace-pre">
{`ch0 = TP2                     # kontrast sonrası ilk faz
ch1 = TP2 - TP1               # kontrast alımı (wash-in)
ch2 = max(TP2..TP_last) - TP1 # zirve yükselişi
ch3 = TP_last - TP2           # kontrast boşalımı (wash-out)`}
        </pre>
        <p className="text-sm">
          AMBL’de <Code>TP_last = TP5</Code> (5 fazlı çekim). BREAST-DIAGNOSIS’ta{' '}
          <Code>TP_last = TP8</Code> (8 fazlı çekim). Yani aynı 4 anlam farklı sayıda
          fazdan üretilir. Her kanal ayrı ayrı z-skor normalize edilir.
        </p>
        <p className="text-sm">
          Bu kanal tasarımı, ham 5 kanallı doğrudan beslemeden (Faz 4 v0) belirgin biçimde
          daha iyi performans verdi: 0.835 → 0.859 ortalama AUC. Ham çoklu kanal,
          MAMA-MIA üzerinde tek kanallı eğitilmiş kodlayıcının dağılımına uymuyor;
          modelin 0-1. epoch aralığında aşırı uyum yapıp genelleyememesine yol açtı.
        </p>
      </Sec>

      <Sec id="siniflandirici" title="Sınıflandırıcı mimarisi">
        <p>
          Mimari, segmentasyon modeliyle aynı kodlayıcıyı kullanır; sınıflandırma
          başlığı sığdır.
        </p>
        <pre className="bg-surface-2 rounded p-3 text-[11px] sm:text-xs overflow-x-auto leading-relaxed whitespace-pre">
{`encoder = PlainConvUNet.encoder            # 6 aşama, 320 kanal
encoder.return_skips = False               # bottleneck çıkışı
                                            #   → (B, 320, 3, 5, 5)
head = nn.Sequential(
  nn.AdaptiveAvgPool3d(1),                 # → (B, 320, 1, 1, 1)
  nn.Flatten(),                            # → (B, 320)
  nn.Dropout(0.3),
  nn.Linear(320, 64), nn.ReLU(),
  nn.Dropout(0.3),
  nn.Linear(64, 1),                        # → logit
)`}
        </pre>
        <p className="text-sm">
          Kodlayıcı 4 kanallı girdi alır; segmentasyon ön eğitimi ise 1 kanal üzerinden
          yapılmıştır. Çok kanallı kodlayıcının ilk konvolüsyonu şöyle başlatılır:
        </p>
        <pre className="bg-surface-2 rounded p-3 text-[11px] sm:text-xs overflow-x-auto leading-relaxed whitespace-pre">
{`new_stem = pretrained_stem.repeat(1, 4, 1, 1, 1) / 4`}
        </pre>
        <p className="text-sm">
          Bu seçim ölçeği korur: 4 kanal aynı değerleri taşıyorsa yeni konvolüsyon
          eski 1 kanallı çıktıyla aynı aktivasyonu üretir. Yani başlangıçta model AMBL ön
          eğitiminin davranışını birebir taklit eder, eğitim sırasında kanallara
          özelleşir.
        </p>
        <p className="text-sm">
          Kodlayıcının aşamalarından{' '}
          <Code>[0, 4, 5]</Code> (stem ve son iki aşama) eğitilebilir,{' '}
          <Code>[1, 2, 3]</Code> dondurulur. Bu, küçük veri kümesinde aşırı öğrenmeyi
          engellerken üst aşamaların kinetik kanallara ince ayar yapmasına izin verir.
        </p>
      </Sec>

      <Sec id="egitim" title="Eğitim yapılandırması">
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Optimizasyon: AdamW, ayrı parametre grupları.</li>
          <li>Kodlayıcı öğrenme oranı: <Code>1e-4</Code>, baş öğrenme oranı: <Code>1e-3</Code>.</li>
          <li>Ağırlık düşürme: <Code>1e-4</Code>.</li>
          <li>Zamanlayıcı: <Code>CosineAnnealingLR</Code>, <Code>T_max = epochs</Code>.</li>
          <li>Parti boyutu: 8.</li>
          <li>Epoch sayısı: 30 (8 epoch sabırla erken durdurma).</li>
          <li>
            Kayıp: <Code>BCEWithLogitsLoss</Code>. Sınıf dengesizliği için{' '}
            <Code>pos_weight = n_neg / n_pos</Code> her kat için ayrı hesaplanır.
          </li>
          <li>Augmentasyonlar: rastgele Y ve X eksenli yansımalar.</li>
          <li>Sabit tohum: <Code>12345</Code>.</li>
          <li>
            Ortalama eğitim süresi: 5 kat için yaklaşık 22 dakika (RTX PRO 6000
            Blackwell).
          </li>
        </ul>
      </Sec>

      <Sec id="cv" title="Çapraz doğrulama yapısı">
        <p>
          Tüm sınıflandırıcı sürümleri 5 katlı çapraz doğrulama ile değerlendirilir.
          Aşağıdaki üç koşul aynı anda sağlanır:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            <strong className="text-text">Hasta gruplaması.</strong> Aynı hastanın tüm
            lezyonları aynı kata düşer. Aksi halde model bir hastanın bir lezyonunu
            görüp komşusunu test ederek yapay yüksek skor üretir.
          </li>
          <li>
            <strong className="text-text">Sınıf katmanlaması.</strong> Her katta
            iyi/kötü huylu oranı toplam dağılıma yakın olur.
          </li>
          <li>
            <strong className="text-text">Kombine modelde kohort × etiket
            katmanlaması.</strong> Beş anahtar (AMBL-iyi, AMBL-kötü, AMBL-karma,
            BD-iyi, BD-kötü) dengeli dağıtılır. Her kat yaklaşık 30 AMBL + 9 BD doğrulama
            kaydı içerir.
          </li>
        </ul>
        <p className="text-sm">
          Her çalıştırmada hem kat başına AUC hem de tüm kat tahminlerinin tek havuza
          alınmasıyla hesaplanan <em>havuzlanmış AUC</em> raporlanır. İki sayı 0.05’ten fazla
          ayrılırsa modelin katlar arası ölçek kaymasına işaret eder; havuzlanmış AUC, gerçek
          dağıtım davranışına daha yakındır.
        </p>
      </Sec>

      <Sec id="tta" title="Test zamanı artırma ve kalibrasyon">
        <p>
          Çıkarımda dört yansıma varyantı tek tek modele verilir, sigmoid çıktıları
          ortalanır. Varyantlar kümesi:
        </p>
        <pre className="bg-surface-2 rounded p-3 text-[11px] sm:text-xs overflow-x-auto leading-relaxed whitespace-pre">
{`identity, flip-Y, flip-X, flip-Y+X`}
        </pre>
        <p className="text-sm">
          AMBL modelinde TTA ortalama AUC’ye <Code>+0.008</Code> kazandırdı (0.859 →
          0.867). Görüntü üzerinde küçük ama tutarlı bir kazanım sağlar.
        </p>
        <p className="text-sm">
          Olasılık kalibrasyonu için kat-dışı Platt ölçeklemesi (lojistik regresyon,
          leave-one-fold-out) denendi. AUC değişmedi; Brier skorunda iyileşme görülmedi
          ya da küçük bir bozulma oldu. Kalibrasyon sorununun kaynağı tek başına bir
          sigmoid eğrisinin değil, kat başına ölçek farkının olması. Platt yönteminin
          v1’de bırakılmamasına karar verildi.
        </p>
      </Sec>

      <Sec id="skor-tablosu" title="Tüm denemeler skor tablosu">
        <p className="text-sm mb-3">
          Kabul edilen ve reddedilen tüm yaklaşımlar tek tabloda. Reddedilen
          denemelerin de yer alması, hangi yolların denendiğini ve neden seçilmediğini
          görmek isteyen okuyucular içindir.
        </p>
        <p className="text-[11px] text-text-dim mb-2 sm:hidden">
          Tabloyu yatayda kaydırarak diğer sütunları görebilirsiniz.
        </p>
        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <table className="text-xs min-w-[640px] w-full">
            <thead className="text-text-dim">
              <tr className="border-b border-border">
                <th className="text-left py-1.5 pr-3">Deneme</th>
                <th className="text-left py-1.5 pr-3">Yaklaşım</th>
                <th className="text-right py-1.5 pr-3">AUC</th>
                <th className="text-right py-1.5 pr-3">Brier</th>
                <th className="text-left py-1.5">Sonuç</th>
              </tr>
            </thead>
            <tbody className="text-text-dim">
              <Row id="3a" name="Doğrusal prob (3a)" auc="0.671 ± 0.095" brier="0.335" v="taban" desc="Dondurulmuş kodlayıcı + lojistik regresyon. Üst sınır deneyi." />
              <Row id="3b" name="Radyomik + XGBoost (3b)" auc="0.817 ± 0.035" brier="0.179" v="rakip" desc="Klasik yaklaşım; beklenenden güçlü taban." />
              <Row id="3c" name="İnce ayar (3c)" auc="0.848 ± 0.049" brier="-" v="kabul" desc="Üst 2 kodlayıcı aşaması açıldı. v1 ara kazananı." />
              <Row id="3d" name="Topluluklar (3d)" auc="-" brier="-" v="reddedildi" desc="Tek model ortalama, 3c+3b ortalama, vb. Hepsi 3c’den kötü." />
              <Row id="4v0" name="Ham 5 kanal (4 v0)" auc="0.835 ± 0.041" brier="-" v="reddedildi" desc="5 fazı doğrudan kanal olarak vermek 3c’den kötüye gitti. 1 kanaldan ön eğitilmiş kodlayıcı çoklu kanala genelleyemedi." />
              <Row id="4v2" name="Kinetik kanallar (4-v2)" auc="0.859 ± 0.048" brier="0.219" v="kabul" desc="Tasarlanmış kanal seçimi v1 final. AMBL’de en yüksek skor." />
              <Row id="4v2tta" name="4-v2 + TTA (1.5)" auc="0.867 ± 0.048" brier="0.219" v="AMBL kazananı" desc="4 yansıma TTA. AMBL-modu için yayım sürümü." />
              <Row id="4v3" name="ConvLSTM3D (4-v3)" auc="0.846 ± 0.025" brier="0.203" v="reddedildi" desc="Zamansal mimari; AUC olarak 4-v2’den düşük, kat standart sapması en küçük. Küçük n’de tasarlanmış kinetik kanallar öğrenilmiş zamansal yapıdan iyi." />
              <Row id="cross" name="Kohortlar arası (BD)" auc="0.457" brier="-" v="başarısız" desc="AMBL’de eğitilmiş model BD’de çöktü. Tüm BD tahminleri 0.5 civarına yığıldı." />
              <Row id="v16" name="Kombine kohort (v1.6)" auc="0.798 ± 0.055" brier="0.244" v="kombine kazananı" desc="AMBL ∪ BD eğitimi; BD AUC 0.457 → 0.571, AMBL 0.867 → 0.833. Kısmi düzelme." />
            </tbody>
          </table>
        </div>
      </Sec>

      <Sec id="kohort-analiz" title="Kohortlar arası davranış">
        <p>
          AMBL modelini BREAST-DIAGNOSIS verisine uyguladığımızda model belirgin
          biçimde başarısız oldu. Olayın anatomisi şu:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            BD malign hastalarda ortalama tahmin <Code>P(M) = 0.496</Code>; BD iyi huylu
            hastalarda <Code>P(M) = 0.497</Code>. İki sınıf arasındaki fark ihmal
            edilebilir.
          </li>
          <li>
            Sınıf içi standart sapma <Code>0.034</Code>; sınıflar arası standart sapma{' '}
            <Code>0.001</Code>. Yani modelin ürettiği fark gürültüye daha yakın.
          </li>
          <li>
            Bu örüntü, "tahminler iki sınıf arasında çift modlu değil" şeklinde,
            tipik olarak eksik alan aktarımının imzasıdır.
          </li>
        </ul>
        <p className="text-sm">
          Kombine modelde aynı analiz yapıldığında BD’nin AUC’si kat başına 0.571,
          havuzlanmış ölçümde 0.523 oluyor. Yani 0.5’in hemen üstüne çıkıyor. Mutlak yer
          değiştirme kayda değer ama şart edilen "BD AUC ≥ 0.65" hedefi karşılanamadı.
        </p>
        <p className="text-sm">
          Bu davranışın nedenleri büyük olasılıkla: (a) MR cihazı protokolü farklılığı
          (BD’nin BLISS_AUTO 8 fazlı protokolü AMBL’nin ortalama 5 fazlı protokolüyle
          aynı dağılıma sahip değil), (b) BD’nin maskeleri Sezi’nin kendi segmentasyon
          modeli tarafından üretildiğinden, ROI merkezleri ortalama 30 bağlı bileşenin
          en büyüğüne göre alınıyor; bu seçim her zaman gerçek lezyonu seçmeyebiliyor,
          (c) eğitim
          dengesizliği (165 AMBL kaydı ve 45 BD kaydı birlikte eğitimde AMBL’yi baskın
          tutuyor).
        </p>
      </Sec>

      <Sec id="yayim" title="Yayım önerisi">
        <p>
          v1 için iki model birlikte yayımlanmıştır:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            <strong className="text-text">AMBL modeli (4-v2 + TTA).</strong> AMBL
            dağılımına yakın görüntüler için önerilir. AUC kat ortalaması 0.867.
            Kullanım koşulu: "Yalnızca AMBL benzeri çekimlerde, kohortlar arası
            doğrulama yok".
          </li>
          <li>
            <strong className="text-text">Kombine model (v1.6).</strong> Karışık veya
            bilinmeyen kaynaklı görüntüler için önerilir. AMBL’de biraz daha düşük doğruluk
            (kat ortalaması 0.833), buna karşın BD’de en az çöküş (0.571).
          </li>
          <li>
            <strong className="text-text">Hiçbir model AMBL ve BD dışındaki kaynaklarda
            uygulanmamalıdır.</strong> Yeni bir kohorta uygulanmadan önce v2 çalışması
            (kohort-farkındalıklı normalizasyon, kendi kendine denetimli ön eğitim, yoğunluk
            uyumlama) gereklidir.
          </li>
        </ul>
      </Sec>

      <Sec id="3b-yapim" title="3B yüzey oluşturma">
        <p>
          Segmentasyon maskesinden 3B model üretimi şu adımlardan oluşur:
        </p>
        <ol className="list-decimal pl-5 space-y-1 text-sm">
          <li>
            Maske RAS’a yeniden yönlendirilir, en büyük bağlı bileşen seçilir (BD’de
            ortalama 30 bileşen vardır; demoda en büyüğünü tutuyoruz).
          </li>
          <li>
            <ExtLink href="https://scikit-image.org/">scikit-image</ExtLink>{' '}
            <Code>marching_cubes</Code> ile <Code>level=0.5</Code> izoyüzeyi çıkarılır.
          </li>
          <li>
            Köşeler voksel uzayından fiziksel milimetre uzayına dönüştürülür: voksel
            indeksi → görüntü başlangıcı + yön matrisi × aralık. Maskenin boyut sırası
            ile SimpleITK’nin <Code>(X, Y, Z)</Code> sırası farklı olduğu için eksen
            değişimi yapılır.
          </li>
          <li>
            Yüzey ağı,{' '}
            <ExtLink href="https://github.com/mikedh/trimesh">trimesh</ExtLink>{' '}
            <Code>filter_taubin(λ=0.5, μ=−0.53, iter=10)</Code> ile yumuşatılır. Taubin
            yöntemi pozitif ve negatif Laplas adımlarını sırayla uygular; saf Laplas
            yumuşatmasından çok daha az hacim kaybı verir.
          </li>
          <li>
            Sonuç <ExtLink href="https://www.khronos.org/gltf/">glTF 2.0</ExtLink>{' '}
            (.glb) olarak dışa aktarılır. Hacim, yüzey alanı, küresellik (sphericity) ve
            kapalı yüzey olup olmadığı (watertight) meta veri olarak yazılır.
          </li>
        </ol>
      </Sec>

      <Sec id="yapi" title="Sezi mimarisi">
        <p>
          İş akışı iki çalışma modunda kullanılabilir:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            <strong className="text-text">Demo modu.</strong> Önceden işlenmiş 8 vakalık
            paket sunulur. Sunucu yalnızca FastAPI + Uvicorn + python-multipart
            içerir; toplam 50 MB civarındadır. Hiçbir ML kütüphanesi gerektirmez,
            herhangi bir CPU üzerinde çalışır.
          </li>
          <li>
            <strong className="text-text">Canlı mod.</strong> Tam ML yığını yüklenir
            (PyTorch, nnU-Net, SimpleITK, trimesh, scikit-image). Kullanıcı DICOM zip
            yükleyince segmentasyon, kinetik yama çıkarımı, iki sınıflandırıcı tahmini
            ve 3B yüzey üretimi gerçek zamanlı yapılır. GPU gerekir.
          </li>
        </ul>
        <p className="text-sm">
          Mod seçimi <Code>INFERENCE_MODE</Code> ortam değişkeni ile yapılır. Hatalı
          yapılandırma durumunda sunucu sessizce demo moduna düşmez; ön doğrulama
          kontrolleri eksik bağımlılıkları listeleyip başlamayı reddeder.
        </p>
      </Sec>

      <Sec id="bilinen" title="Bilinen sınırlamalar (detaylı)">
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li>
            <strong className="text-text">Veri ölçeği.</strong> 165 AMBL lezyonu + 45 BD
            hastası, derin öğrenme için küçük bir küme. Kat standart sapması 0.05 civarında
            kalıyor; gerçek bir popülasyonda performansın bu sınırların 1-2 standart
            sapma dışına çıkması mümkün.
          </li>
          <li>
            <strong className="text-text">Kalibrasyon.</strong> Modelin ürettiği
            olasılıklar 0.5 etrafında yığılıyor. Sıralama sağlam ama mutlak değer
            yanıltıcı: %70 demek "ortalama %70 ihtimal" anlamına gelmeyebilir. Havuzlanmış
            AUC ile kat-ortalama AUC arasındaki fark (örn. AMBL’de 0.867 ve 0.720)
            bunun ölçülebilir göstergesidir.
          </li>
          <li>
            <strong className="text-text">Aşırı segmentasyon.</strong> BD görüntülerinde
            segmentasyon modeli hasta başına ortalama 30 bağlı bileşen üretiyor. En
            büyüğünün gerçek lezyon olduğu varsayımı her zaman doğru değil. v1’de bu
            kabul ediliyor; v1.7 olarak hacim eşikli bileşen filtresi öneriliyor.
          </li>
          <li>
            <strong className="text-text">Tam kaçırma.</strong> Segmentasyon modeli bazı
            (özellikle merkez dışı, multifokal) lezyonları tamamen kaçırıyor. Bu
            durumda 3B model üretilemiyor, sınıflandırıcı için yedek olarak görüntü
            merkezinden bir yama kullanılıyor; doğal olarak güvenilirlik düşük.
          </li>
          <li>
            <strong className="text-text">Tek tohum.</strong> Tüm sayılar tek bir
            rastgelelik tohumundan (12345) gelir. Çoklu tohum ortalaması v1.7 olarak
            önerilmiştir; 3 tohum × 5 kat ile kat standart sapması küçülecek.
          </li>
          <li>
            <strong className="text-text">DICOM zip varsayımları.</strong> Canlı mod,
            yüklenen zip’in tek bir DCE-MRI çalışmasının dosyalarından oluştuğunu ve
            serilerin <Code>AcquisitionTime</Code> alanı ile sıralanabilir olduğunu
            varsayar. Eksik veya bozuk meta veri varsa otomatik tespit hatalı olabilir.
          </li>
        </ul>
      </Sec>

      <Sec id="v2" title="v2 yol haritası">
        <p>
          v1’de gözlenen sınırlamaları aşmak için v2 çalışmalarında planlananlar:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            <strong className="text-text">Kohort-farkındalıklı normalizasyon.</strong>
            Kohort meta verisine koşullanan ConditionalNormalisation veya kohort başına
            ayrı BatchNorm istatistikleri.
          </li>
          <li>
            <strong className="text-text">Kendi kendine denetimli ön eğitim.</strong>
            AMBL ∪ BD ∪ MAMA-MIA üzerinde SSL ile kohort kısayolları öğrenmeyen ve daha
            iyi genelleyen bir kodlayıcı.
          </li>
          <li>
            <strong className="text-text">Yoğunluk uyumlama.</strong>
            CycleGAN tarzı kohort dönüştürme ön işlemesiyle BD görüntülerini AMBL
            dağılımına yaklaştırma.
          </li>
          <li>
            <strong className="text-text">İki aşamalı dedektör + segmentasyon modeli.</strong>
            Önce küçük bir lezyon dedektörü, sonra dedektörün önerdiği bölgede
            segmentasyon. Multifokal ve kenar lezyonları için.
          </li>
          <li>
            <strong className="text-text">Çoklu tohum + çoklu kat ortalaması.</strong>
            Mevcut standart sapmanın gerçekten ne kadarı modelin belirsizliği, ne kadarı
            örnekleme gürültüsü ayrımı için.
          </li>
          <li>
            <strong className="text-text">Ölçek farkındalıklı kayıp.</strong> Segmentasyon
            modelindeki tam kaçırma vakalarının kabaca tamamı küçük lezyonlar; küçük
            lezyona ağırlık veren kayıp veya çoklu ölçek FPN tarzı bir omurga
            öneriliyor.
          </li>
        </ul>
      </Sec>

      <Sec id="kaynak-kontrolu" title="Çoğaltılabilirlik">
        <p>
          Tüm sayılar üretilirken sabit tohum, dondurulmuş bağımlılık sürümleri ve kat
          atamaları kullanılmıştır. Aynı eğitim setiyle aynı kontrol noktaları yeniden
          üretilebilir. nnU-Net plan dosyaları ve sınıflandırıcı kontrol noktaları
          versiyonlanmıştır; her sınıflandırıcı denemesi için bağımsız bir konfigürasyon
          ve metrik dosyası saklanır.
        </p>
        <p className="text-sm">
          Kullanılan veri kümeleri kendi yayın koşullarına tabidir; AMBL ve
          BREAST-DIAGNOSIS,{' '}
          <ExtLink href="https://www.cancerimagingarchive.net/data-usage-policies-and-restrictions/">
            TCIA veri kullanım politikası
          </ExtLink>{' '}
          altında erişilebilir. MAMA-MIA için Synapse erişim hesabı ve atıf
          gereksinimleri vardır.
        </p>
      </Sec>
    </div>
  )
}

function Sec({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl font-medium text-text mb-3 border-b border-border pb-2">
        {title}
      </h2>
      <div className="space-y-3 text-text-dim leading-relaxed">{children}</div>
    </section>
  )
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="text-text">{children}</code>
}

function Row({
  name,
  auc,
  brier,
  v,
  desc,
}: {
  id: string
  name: string
  auc: string
  brier: string
  v: string
  desc: string
}) {
  const verdictCls =
    v === 'AMBL kazananı' || v === 'kombine kazananı' || v === 'kabul'
      ? 'text-benign'
      : v === 'reddedildi' || v === 'başarısız'
      ? 'text-malignant'
      : 'text-text-dim'
  return (
    <tr className="border-b border-border/40 align-top">
      <td className="py-2 pr-3 text-text whitespace-nowrap">{name}</td>
      <td className="py-2 pr-3">{desc}</td>
      <td className="py-2 pr-3 text-right font-mono whitespace-nowrap text-text">{auc}</td>
      <td className="py-2 pr-3 text-right font-mono text-text">{brier}</td>
      <td className={`py-2 ${verdictCls} whitespace-nowrap text-xs uppercase tracking-wider`}>
        {v}
      </td>
    </tr>
  )
}
